import type { SupportedLocale } from "~/i18n/types";
import { defaultLocale, supportedLocales } from "~/i18n/types";

// Language codes a browser reports for a language the locale list names by another code.
// Norwegian ships as the macrolanguage "no", while browsers identify as one of its two
// written standards: Bokmål ("nb") or Nynorsk ("nn").
const LANGUAGE_ALIASES: Record<string, SupportedLocale> = {
  nb: "no",
  nn: "no",
};

/**
 * Try to match a language code to a supported locale
 * First tries exact match, then language prefix match, then a language alias
 */
function matchSupportedLocale(code: string): SupportedLocale | null {
  // Callers hand us lower-cased codes while the locale list keeps its canonical casing
  // ("zh-CN", "zh-TW"), so both sides are compared lower-cased and the list's own entry
  // is what gets returned.
  const wanted = code.toLowerCase();

  // Try exact match first (e.g., "es", "fr", "zh-CN")
  const exact = supportedLocales.find((locale) => locale.toLowerCase() === wanted);
  if (exact) {
    return exact;
  }

  // Try language prefix (e.g., "en-US" -> "en", "zh-Hans" -> "zh")
  const langPrefix = wanted.split("-")[0];
  const byPrefix = supportedLocales.find((locale) => locale.toLowerCase() === langPrefix);
  if (byPrefix) {
    return byPrefix;
  }

  return Object.hasOwn(LANGUAGE_ALIASES, langPrefix) ? LANGUAGE_ALIASES[langPrefix] : null;
}

/**
 * Parse Accept-Language header and return the best matching supported language
 */
export function detectLanguageFromHeader(acceptLanguageHeader?: string): SupportedLocale {
  if (!acceptLanguageHeader) {
    return defaultLocale;
  }

  // Parse Accept-Language header format: "en-US,en;q=0.9,es;q=0.8"
  const languages = acceptLanguageHeader
    .split(",")
    .map((lang) => {
      const parts = lang.trim().split(";");
      const code = parts[0].toLowerCase();
      const quality = parts[1] ? Number.parseFloat(parts[1].split("=")[1]) : 1.0;
      return { code, quality };
    })
    .toSorted((a, b) => b.quality - a.quality); // Sort by quality score

  // Find first supported language
  for (const { code } of languages) {
    const matched = matchSupportedLocale(code);
    if (matched) {
      return matched;
    }
  }

  return defaultLocale;
}

/**
 * Detect language on client-side using navigator.language
 * Uses the primary browser language (navigator.language)
 */
export function detectClientLanguage(): SupportedLocale {
  if (typeof navigator === "undefined") {
    return defaultLocale;
  }

  // Use primary browser language (navigator.language)
  const primaryLang = navigator.language?.toLowerCase() || "";

  const matched = matchSupportedLocale(primaryLang);
  return matched || defaultLocale;
}

/**
 * Extract language preference from cookie header
 * Parses the PARAGLIDE_LOCALE cookie value and validates it against supported locales
 * @param cookieHeader - The cookie header string from the HTTP request
 * @returns The supported locale if found and valid, null otherwise
 */
export function getLanguageFromCookie(cookieHeader: string): SupportedLocale | null {
  if (!cookieHeader) {
    return null;
  }

  // Parse PARAGLIDE_LOCALE cookie (matches paraglide's cookie name)
  const cookieMatch = cookieHeader.match(/PARAGLIDE_LOCALE=([^;]+)/);
  if (!cookieMatch?.[1]) {
    return null;
  }

  const cookieLocale = cookieMatch[1].trim();
  if (supportedLocales.includes(cookieLocale as SupportedLocale)) {
    return cookieLocale as SupportedLocale;
  }

  return null;
}
